import React, { useRef, useState, useEffect, useCallback } from "react";
import { keyframes } from "@emotion/react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  IconButton,
  Divider,
  Slider,
  TextField,
  Button
} from "@mui/material";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import BreatheRight from "../assets/BreatheRight.png";
import { submitCustomerFeedback } from "../services/customerFeedbackService";
import { markSessionMusicCompleted } from "../services/customerSessionDetails";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AppleIcon from "@mui/icons-material/Apple";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MusicNoteIcon from "@mui/icons-material/MusicNote";

const LANG_CODE_ALIASES = {
  english: "eng",
  en: "eng",
  eng: "eng",
  hindi: "hin",
  hi: "hin",
  hin: "hin",
  bengali: "ben",
  bangla: "ben",
  bn: "ben",
  ben: "ben",
  tamil: "tam",
  ta: "tam",
  tam: "tam",
};

const normalizeLangCode = (code) => {
  if (!code) return "eng";
  const key = String(code).trim().toLowerCase();
  return LANG_CODE_ALIASES[key] || key;
};

const therapyCopy = {
  eng: {
    recovery: "Wishing you a gentle and quick recovery ahead.",
    support: "echoCare is here to bring comfort and care throughout",
  },
  hin: {
    recovery: "आपके शीघ्र और सहज स्वास्थ्य लाभ की कामना करते हैं।",
    support: "echoCare आपकी पूरी यात्रा में आराम और देखभाल देने के लिए साथ है।",
  },
  ben: {
    recovery: "আপনার দ্রুত ও স্বস্তিদায়ক আরোগ্য কামনা করি।",
    support: "echoCare আপনার পুরো যাত্রায় সান্ত্বনা ও যত্ন দিতে পাশে আছে।",
  },
  tam: {
    recovery: "நீங்கள் விரைவாகவும் மென்மையாகவும் குணமடைய வாழ்த்துகிறோம்.",
    support: "echoCare உங்கள் பயணம் முழுவதும் ஆறுதலும் பராமரிப்பும் வழங்க இங்கே உள்ளது.",
  },
};


const pulseGlow = keyframes`
    0%, 100% { 
        box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
        transform: scale(1);
    }
    50% { 
        box-shadow: 0 0 40px rgba(255, 255, 255, 0.4);
        transform: scale(1.05);
    }
`;

const ApolloMusicTherapy = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const storedCustomer = JSON.parse(localStorage?.getItem("customer") || "{}");
  const package_type = storedCustomer?.package_type;
  const languageCode = normalizeLangCode(storedCustomer?.preferred_language);
  const localizedCopy = therapyCopy[languageCode] || therapyCopy.eng;


  const song = storedCustomer?.track_url
    ? (() => {
      let src = storedCustomer?.track_url

      console.log("🎵 Song path:", src);

      return {
        title: storedCustomer?.track_name || "Unknown Track",
        src,
        theme: "theme1",
        icon: BreatheRight,
      };
    })()
    : null;

  // Core player state (keeps re-renders minimal)
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSlider, setShowSlider] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [volume, setVolume] = useState(1);
  const [favorites, setFavorites] = useState([]);

  const [showSessionComplete, setShowSessionComplete] = useState(false);

  const [sessionComplete, setSessionComplete] = useState(() => {
    return localStorage?.getItem("sessionComplete") === "true";
  });
  const [showDownload, setShowDownload] = useState(false);

  const [comments, setComments] = useState("");
  const [selectedMood, setSelectedMood] = useState("");




  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);


  // refs for audio + UI elements we mutate directly
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const currentTimeRef = useRef(null);
  const durationRef = useRef(null);
  const rafRef = useRef(null);
  const durationValueRef = useRef(0); // store duration (seconds) in ref
  const isMountedRef = useRef(false);

  // animation elements state (kept as array state since changes are infrequent)
  const [animationElements, setAnimationElements] = useState([]);


  const [relaxationRating, setRelaxationRating] = useState(0);
  const [musicRating, setMusicRating] = useState(0);




  const playNext = useCallback(() => {
    // Only one song → always index 0
    setCurrentIndex(0);
    setIsPlaying(true);
  }, [isShuffle]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      cancelAnimationFrame(rafRef.current);
      setIsPlaying(false);
    } else {
      audio.play().catch(() => { });
      startProgressLoop(); // ensure RAF loop running
      setIsPlaying(true);
    }
  }, [isPlaying]);

  // Utility: format seconds to M:SS
  const formatTime = (seconds = 0) => {
    if (!seconds || Number.isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // Update audio src when currentIndex changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // set src and load
    audio.src = song?.src;
    audio.load();

    // apply volume
    audio.volume = volume;

    // if should play, attempt to play
    if (isPlaying) {
      audio
        .play()
        .then(() => {
          // started
          startProgressLoop();
        })
        .catch(() => {
          // autoplay blocked — UI still shows paused until user clicks
        });
    } else {
      // ensure we reset UI if paused
      cancelAnimationFrame(rafRef.current);
      // set progress UI to 0 if new track and not playing
      if (progressRef.current) progressRef.current.style.width = "0%";
      if (currentTimeRef.current) currentTimeRef.current.textContent = "0:00";
      if (durationRef.current) durationRef.current.textContent = song.time || "0:00";
      durationValueRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]); // deliberately not including isPlaying/volume here to decouple

  // When volume state changes update audio element
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // onLoadedMetadata: set duration ref
  const onLoadedMetadata = (e) => {
    const audio = e.target;
    const dur = audio.duration || 0;
    durationValueRef.current = dur;
    if (durationRef.current) durationRef.current.textContent = formatTime(dur);
  };

  // useEffect(() => {
  //   const saved = localStorage.getItem("sessionComplete");
  //   if (saved === "true") {
  //     setSessionComplete(true);
  //   }
  // }, []);

  // onEnded: handle repeat or next
  const onEnded = () => {
    if (isRepeat) {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(() => { });
      }
    } else {
      setIsPlaying(false);
      setSessionComplete(true);
      localStorage.setItem("sessionComplete", "true");

      const updatedCustomer = JSON.parse(localStorage.getItem("customer") || "{}");
      if (updatedCustomer.customer_session_id) {
        updatedCustomer.music_completed = true;
        localStorage.setItem("customer", JSON.stringify(updatedCustomer));
        markSessionMusicCompleted(updatedCustomer.customer_session_id).catch((err) =>
          console.error("Failed to mark session complete:", err)
        );
      }
    }
  };

  // Start requestAnimationFrame loop to update UI (progress bar and current time) without setState per frame
  const startProgressLoop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const loop = () => {
      const dur = durationValueRef.current || audio.duration || 0;
      const cur = audio.currentTime || 0;
      // update progress bar width (read-only)
      if (progressRef.current) {
        const pct = dur ? (cur / dur) * 100 : 0;
        progressRef.current.style.width = `${pct}%`;
      }
      // update current time label
      if (currentTimeRef.current) currentTimeRef.current.textContent = formatTime(cur);

      rafRef.current = requestAnimationFrame(loop);
    };

    // ensure any previous RAF canceled
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  // Clean up RAF on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Attach audio listeners once (loadedmetadata + ended)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // When play state toggles to true, ensure progress loop runs
  useEffect(() => {
    if (isPlaying) startProgressLoop();
    else cancelAnimationFrame(rafRef.current);
  }, [isPlaying, startProgressLoop]);

  // Background gradient generator (kept from your original)
  const getBackgroundGradient = () => {
    // if (!isPlaying) {
    //   return "linear-gradient(145deg, #4fc3f7 0%, #0277bd 100%)";
    // }

    const currentTheme = song?.theme;
    switch (currentTheme) {
      case "theme1":
        return "linear-gradient(145deg, #80cbc4 0%, #00695c 100%)";
      case "theme2":
        return "linear-gradient(145deg, #f48fb1 0%, #c2185b 100%)";
      case "theme3":
        return "linear-gradient(135deg, #a855f7 0%, #9333ea 25%, #7c3aed 50%, #6d28d9 75%, #5b21b6 100%)";
      default:
        return "linear-gradient(145deg, #4fc3f7 0%, #0277bd 100%)";
    }
  };

  // const isDisabled =
  //   !painScale ||
  //   selectedExperiences.length === 0 ||
  //   therapyContinue === null ||
  //   !selectedMood ||
  //   !selectedRelaxation ||
  //   !selectedEffectiveness;
  //fedback new
  const isDisabled =
    relaxationRating === 0 ||
    musicRating === 0 ||
    !selectedMood;


  // const handleSubmitFeedback = async () => {
  //   try {
  //     const payload = {
  //       session_detail_id: storedCustomer?.customer_session_id, // 🔥 replace with dynamic ID later
  //       pain_level: Number(painScale),
  //       mood: selectedMood,
  //       relaxation_level: selectedRelaxation,
  //       session_feedback: selectedEffectiveness,
  //       experience: selectedExperiences,
  //       interested: therapyContinue,
  //       comments: comments
  //     };



  //     console.log("Submitting Payload:", payload);

  //     const response = await submitCustomerFeedback(payload);

  //     console.log("Feedback submitted successfully:", response);
  //     alert("Thank you! Your feedback has been submitted.");
  //     navigate("/my-profile");

  //   } catch (error) {
  //     console.error("Submit Error:", error);
  //     alert("Something went wrong while submitting feedback.");
  //   }
  // };
  const handleSubmitFeedback = async () => {
    if (!storedCustomer?.customer_session_id) {
      alert("Session expired. Please restart therapy.");
      return;
    }

    const moodMap = { "😊": "Happy", "😌": "Relaxed", "😐": "Neutral", "😟": "Sad", "😢": "Very Sad" };

    try {
      const payload = {
        session_detail_id: storedCustomer.customer_session_id,
        mood: moodMap[selectedMood],
        relaxation_level: String(relaxationRating),
        music_rating: String(musicRating),
        comments: comments || "",
      };

      await submitCustomerFeedback(payload);

      localStorage.setItem("musiccDownloadShown", "true");
      localStorage.removeItem("sessionComplete");

      setShowDownload(true); // show Download UI first
    } catch (error) {
      console.error(error.response?.data || error);
      alert(error.response?.data?.error || "Feedback submission failed");
    }
  };



  //   const handleSubmitFeedback = async () => {
  //   if (!storedCustomer?.customer_session_id) {
  //     alert("Session ID missing. Please restart therapy.");
  //     return;
  //   }

  //   const moodMap = {
  //     "😊": "Happy",
  //     "😌": "Relaxed",
  //     "😐": "Neutral",
  //     "😟": "Sad",
  //     "😢": "Very Sad",
  //   };

  //   try {
  //     const payload = {
  //       session_detail_id: storedCustomer.customer_session_id,
  //       mood: moodMap[selectedMood],
  //       relaxation_level: Number(relaxationRating),
  //       music_rating: Number(musicRating),
  //       comments,
  //     };

  //     console.log("Submitting payload:", payload);

  //     await submitCustomerFeedback(payload);

  //     localStorage.setItem("musiccDownloadShown", "true");
  //     localStorage.removeItem("sessionComplete");

  //     setShowDownload(true);
  //   } catch (error) {
  //     console.error("Submit Error:", error.response?.data || error);
  //     alert(error.response?.data?.message || "Feedback submission failed.");
  //   }
  // };
  const handleContinue = () => {
    setShowDownload(true);
  };
  if (showDownload) {
    return (
      <DownloadMusiccUI
        package_type={package_type}
        onContinue={() => {
          setShowDownload(false);
          setShowSessionComplete(true); // show session complete next
        }}
      />
    );
  }
  if (showSessionComplete) {
    return (
      <SessionCompleteUI
        onClose={() => {
          setShowSessionComplete(false);
          navigate("/my-profile"); // final redirect
        }}
      />
    );
  }

  const fieldLabelSx = {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#374151",
    mb: 0.5,
  };


  return sessionComplete ? (
    <>
      {/* ---------- SESSION FEEDBACK ---------- */}
      <Box
        sx={{

          display: "flex",
          boxSizing: "border-box",
          justifyContent: "center",
          alignItems: "flex-start",
          backgroundColor: "#f9fafb",
          py: 2,
          overflow: "hidden",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 600,
            height: { xs: 520, sm: 470 },   // ✅ FIXED HEIGHT
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Box
              component="img"
              src="pen.png"   // 🔁 replace with your actual file name
              alt="Session Feedback"
              sx={{
                width: 25,
                height: 25,
              }}
            />
            Session Feedback
          </Typography>




          <Typography
            sx={{
              fontSize: "0.875rem",
              color: "text.secondary",
              mb: 2,
            }}
          >
            Please share your experience with today's session
          </Typography>

          {/* Relaxation Rating */}
          <Typography sx={fieldLabelSx}>
            How relaxed do you feel?
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <Typography
                key={star}
                onClick={() => setRelaxationRating(star)}
                sx={{
                  fontSize: 26,
                  cursor: "pointer",
                  color: relaxationRating >= star ? "#facc15" : "#e5e7eb",
                }}
              >
                ★
              </Typography>
            ))}
          </Box>

          {/* Mood */}
          <Typography sx={fieldLabelSx}>
            Current Mood
          </Typography>

          <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
            {["😊", "😌", "😐", "😟", "😢"].map((emoji) => (
              <Typography
                key={emoji}
                onClick={() => setSelectedMood(emoji)}
                sx={{
                  fontSize: 26,
                  cursor: "pointer",
                  opacity: selectedMood === emoji ? 1 : 0.4,
                  transform:
                    selectedMood === emoji ? "scale(1.15)" : "scale(1)",
                  transition: "all 0.2s",
                }}
              >
                {emoji}
              </Typography>
            ))}
          </Box>

          {/* Music Rating */}
          <Typography sx={fieldLabelSx}>
            Rate the music selection
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <Typography
                key={star}
                onClick={() => setMusicRating(star)}
                sx={{
                  fontSize: 26,
                  cursor: "pointer",
                  color: musicRating >= star ? "#facc15" : "#e5e7eb",
                }}
              >
                ★
              </Typography>
            ))}
          </Box>

          {/* Comments */}
          <TextField
            placeholder="Share your thoughts..."
            multiline
            rows={2}
            fullWidth
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            sx={{
              mb: 1,
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                color: '#111827',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#9ca3af',
                opacity: 1,
              },
            }}
          />


          <Button
            fullWidth
            disabled={isDisabled}
            onClick={handleSubmitFeedback}
            sx={{
              background: "linear-gradient(90deg, #6f7de8, #7b4bb7)",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
              py: 0.8,
              minHeight: 40,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                opacity: 0.95,
              },
              "&.Mui-disabled": {
                background: "#9ca3af",
                color: "#fff",
              },
            }}
          >
            Submit Feedback
          </Button>



        </Card>
      </Box>
    </>
  ) : (
    (
      <Card
        sx={{
          height: {
            xs: "calc(100vh - 56px)",
            sm: "calc(100vh - 64px)",
            md: "calc(100vh - 70px)",
          },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          background: getBackgroundGradient(),
          color: "#fff",
          borderRadius: 0,
          position: "relative",
          overflowX: "hidden",
          overflowY: "auto",
          overflowY: "hidden",
          transition: "background 1s ease-in-out",
        }}
      >
        {/* Animated Background Elements */}
        {animationElements.map((element) => (
          <Box key={element.id} sx={element.style} />
        ))}

        {/* Left Panel */}
        <Box
          sx={{
            width: { xs: "100%", md: "40%" },
            position: "relative",
            zIndex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* Header (top-left) */}
          <Box
            sx={{
              position: "absolute",
              top: { xs: 4, sm: 14, md: 16 },
              left: { xs: 4, sm: 14, md: 16 },
              zIndex: 5,
              ml: { xs: 2, md: 2 },
              pt: { xs: 2, md: 0 },
            }}
          >
            <Typography
              variant="h5"
              fontWeight={300}
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.3rem", md: "1.5rem" },
              }}
            >
              Music Therapy
            </Typography>
          </Box>

          <CardContent
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              width: "100%",
              mt: { xs: 12, sm: 14, md: 0 },
            }}
          >
            <Box
              sx={{
                width: { xs: "70%", md: "40vw" },
                height: { xs: 180, md: 220 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                mb: 2,
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: { xs: "180px", sm: "200px", md: "220px" },
                  height: { xs: "180px", sm: "200px", md: "220px" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                {[1, 2, 3, 4].map((index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "absolute",
                      borderRadius: "50%",
                      border:
                        song?.theme === "theme1"
                          ? "2px solid rgba(192, 224, 204, 0.35)"
                          : song?.theme === "theme2"
                            ? "2px solid rgba(59, 130, 246, 0.35)"
                            : song?.theme === "theme3"
                              ? "2px solid rgba(168, 85, 247, 0.35)"
                              : "2px solid rgba(255, 255, 255, 0.22)",
                      width: `${index * 60}px`,
                      height: `${index * 60}px`,
                      animation: isPlaying ? `${pulseGlow} ${2 + index * 0.45}s ease-in-out infinite` : "none",
                      animationDelay: `${index * 0.25}s`,
                      opacity: 0.9,
                    }}
                  />
                ))}
              </Box>

              {/* Center Icon */}
              {song && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 3,
                    width: { xs: 60, sm: 70, md: 84 },
                    height: { xs: 60, sm: 70, md: 84 },
                    background:
                      song?.theme === "theme1"
                        ? "linear-gradient(135deg, rgba(34,197,94,0.32), rgba(34,197,94,0.16))"
                        : song?.theme === "theme2"
                          ? "linear-gradient(135deg, rgba(59,130,246,0.32), rgba(59,130,246,0.16))"
                          : song?.theme === "theme3"
                            ? "linear-gradient(135deg, rgba(168,85,247,0.32), rgba(168,85,247,0.16))"
                            : "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2rem",
                    backdropFilter: "blur(6px)",
                    pointerEvents: "auto",
                  }}
                >
                  <Box
                    component="img"
                    src={song?.icon}
                    alt={song?.title}
                    sx={{
                      width: { xs: 30, sm: 36, md: 48 },
                      height: { xs: 30, sm: 36, md: 48 },
                      objectFit: "contain",
                      borderRadius: "50%",
                    }}
                  />
                </Box>
              )}
            </Box>

            <Typography
              variant="body1"
              sx={{
                opacity: 0.9,
                maxWidth: { xs: "92%", md: "80%" },
                mt: { xs: 4, md: 2 },
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
              }}
            >
              {song?.title}
            </Typography>

          </CardContent>
        </Box>

        {/* Right Panel */}
        <CardContent
          sx={{
            width: { xs: "100%", md: "60%" },
            px: { xs: 2, sm: 3, md: 0 },
            py: { xs: 1, sm: 2, md: 0 },
            display: "flex",
            flexDirection: "column",
            mt: { xs: 0, sm: 0, md: 5 },
            marginLeft: { xs: 1 },
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",   // ⭐ vertical center
              height: "100%",             // ⭐ required
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 400 }}>
                {storedCustomer?.track_displayName || "Healing begins with hope, rest, and support."}
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 400 }}>
                {localizedCopy.recovery}
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 400 }}>
                {localizedCopy.support}
              </Typography>
            </Box>


            {/* Music Controls */}
            <Box sx={{ mt: { xs: 2, md: 4 }, display: "flex", justifyContent: "center", width: "100%", pr: { xs: 2, sm: 4 }, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0, backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "28px", padding: "4px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}>

                <IconButton onClick={togglePlayPause} size="large" sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } }}>
                  {isPlaying ? <PauseCircleIcon sx={{ fontSize: 36 }} /> : <PlayCircleIcon sx={{ fontSize: 36 }} />}
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255, 255, 255, 0.2)", height: 24, alignSelf: "center" }} />

                <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                  <IconButton onClick={() => setShowSlider((p) => !p)} sx={{ color: volume === 0 ? "#aaa" : "#fff", mr: 1 }}>
                    <VolumeUpIcon />
                  </IconButton>

                  {/* Vertical Slider */}
                  {showSlider && (
                    <Box sx={{ position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)", height: 120, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.5)", borderRadius: 2, p: 1, zIndex: 10 }}>
                      <Slider
                        orientation="vertical"
                        min={0}
                        max={1}
                        step={0.01}
                        value={volume}
                        onChange={(e, val) => {
                          setVolume(val);
                          if (audioRef.current) audioRef.current.volume = val;
                        }}
                        sx={{ height: 100, color: "#fff", "& .MuiSlider-thumb": { width: 12, height: 12 } }}
                      />
                    </Box>
                  )}
                </Box>

              </Box>
            </Box>

            {/* Audio element (hidden) */}
            <audio
              ref={audioRef}
              src={song?.src}
              preload="metadata"
              onLoadedMetadata={onLoadedMetadata}
              onEnded={onEnded}
              style={{ display: "none" }}
            />

            {/* Progress Row (read-only) */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              {/* <Typography variant="caption" sx={{ minWidth: 35 }} ref={currentTimeRef}>
                0:00
              </Typography> */}

              <Box sx={{ flex: 1, height: 5, mx: 2, bgcolor: "rgba(255,255,255,0.3)", borderRadius: 5, position: "relative", cursor: "default", overflow: "hidden" }}>
                <Box ref={progressRef} sx={{ height: "100%", width: "0%", background: "linear-gradient(90deg, #667eea, #764ba2)", transition: "width 0.08s linear" }} />
              </Box>

              {/* <Typography variant="caption" sx={{ minWidth: 35 }} ref={durationRef}>
                {song.time || "0:00"}
              </Typography> */}
            </Box>
          </Box>
        </CardContent>
      </Card>
    )
  );


};



const DownloadMusiccUI = ({ onContinue, package_type }) => {
  const normalizedPackage = package_type?.replace(/\s+/g, "");
  if (normalizedPackage === "basic") {
    return (

      <Box
        sx={{
          display: "flex",
          boxSizing: "border-box",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f9fafb",
          py: 2,
          overflow: "hidden",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 600,
            height: { xs: 520, sm: 430 },   // ✅ FIXED HEIGHT
            p: { xs: 1, sm: 2 },
            borderRadius: 4,
            background:
              "linear-gradient(180deg, #6f7de8 0%, #7b4bb7 100%)",
            color: "#fff",
            p: 3,
            mb: 4,
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, textAlign: "center", mb: 1 }}
          >
            📲 Download MUSICC Wellness App
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: 16,
              opacity: 0.9,
              mb: 2,
            }}
          >
            Continue your healing journey anytime, anywhere!
          </Typography>

          <Box
            sx={{
              display: "inline-block",        // ⬅️ shrink to content
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              px: 2,
              py: 1.2,
              mt: 1.5,
              mb: 2,
              textAlign: "center",
              backgroundColor: "rgba(255,255,255,0.08)",
              mx: "auto",                     // ⬅️ center horizontally
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 15,
                mb: 0.3,
                whiteSpace: "nowrap",         // ⬅️ prevent wrapping
              }}
            >
              🎁 3 Months FREE
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                opacity: 0.9,
                whiteSpace: "nowrap",         // ⬅️ prevent wrapping
              }}
            >
              App subscription included with your package
            </Typography>
          </Box>

          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: 3,
              backgroundColor: "#fff",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MusicNoteIcon
              sx={{
                fontSize: 80,
                color: "#6f7de8",
              }}
            />
          </Box>

          {/* Store Buttons */}
          {/* Store Buttons */}
          <Stack direction="row" spacing={2} justifyContent="center" mb={2}>
            <Button
              variant="outlined"
              startIcon={<AppleIcon />}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
              }}
              href="https://apps.apple.com/in/app/echo-wellness-sound-sleep/id1585498853"
              target="_blank"
            >
              App Store
            </Button>

            <Button
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
              }}
              href="https://play.google.com/store/apps/details?id=com.diginxthlt.echo"
              target="_blank"
            >
              Google Play
            </Button>
          </Stack>

          <Button
            fullWidth
            onClick={onContinue}
            sx={{
              background: "#fff",
              color: "#4072fc",
              fontSize: "14px",
              fontWeight: 600,
              py: 0.8,
              minHeight: 40,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                opacity: 0.95,
              },
              "&.Mui-disabled": {
                background: "#9ca3af",
                color: "#fff",
              },
            }}
          >
            Continue →
          </Button>
        </Card>
      </Box>
    );
  }

  if (normalizedPackage === "premium") {
    return (

      <Box
        sx={{
          display: "flex",
          boxSizing: "border-box",
          justifyContent: "center",
          alignItems: "flex-start",
          backgroundColor: "#f9fafb",
          py: 2,
          overflow: "hidden",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 600,
            height: { xs: 520, sm: 520 },   // ✅ FIXED HEIGHT
            p: { xs: 1, sm: 2 },
            borderRadius: 4,
            background:
              "linear-gradient(180deg, #6f7de8 0%, #7b4bb7 100%)",
            color: "#fff",
            p: 3,
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, textAlign: "center", }}
          >
            📲 Download MUSICC Wellness App
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: 14,
              opacity: 0.9,
              mb: 1,
            }}
          >
            Continue your healing journey anytime, anywhere!
          </Typography>

          <Box
            sx={{
              display: "inline-block",        // ⬅️ shrink to content
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              px: 2,
              py: 1.2,
              mt: 1.5,
              mb: 2,
              textAlign: "center",
              backgroundColor: "rgba(255,255,255,0.08)",
              mx: "auto",                     // ⬅️ center horizontally
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 15,
                mb: 0.3,
                whiteSpace: "nowrap",         // ⬅️ prevent wrapping
              }}
            >
              🎁 3 Months FREE
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                opacity: 0.9,
                whiteSpace: "nowrap",         // ⬅️ prevent wrapping
              }}
            >
              App subscription included with your package
            </Typography>
          </Box>
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: 3,
              backgroundColor: "#fff",
              mx: "auto",
              mb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MusicNoteIcon
              sx={{
                fontSize: 50,
                color: "#6f7de8",
              }}
            />
          </Box>

          {/* Store Buttons */}
          {/* Store Buttons */}
          <Stack direction="row" spacing={2} justifyContent="center" mb={2}>
            <Button
              variant="outlined"
              startIcon={<AppleIcon />}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
              }}
              href="https://apps.apple.com/in/app/echo-wellness-sound-sleep/id1585498853"
              target="_blank"
            >
              App Store
            </Button>

            <Button
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
              }}
              href="https://play.google.com/store/apps/details?id=com.diginxthlt.echo"
              target="_blank"
            >
              Google Play
            </Button>
          </Stack>


          {/* Counselling Info */}
          <Box
            sx={{
              background: "rgba(255,255,255,0.15)",
              borderRadius: 3,
              p: 2,                 // ⬅️ more padding
              mb: 2,                // ⬅️ more space below card
            }}
          >
            {/* Title */}
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,              // ⬅️ space below title
                color: "#fff",
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 20 }} />
              Counselling Sessions
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "rgba(255,255,255,0.9)",
                mb: 1.5,            // ⬅️ better spacing
                lineHeight: 1.6,
                textAlign: "left",
              }}
            >
              We will send you a WhatsApp link to book your counselling slots.
            </Typography>

            {/* Points */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "0.9rem", color: "#fff" }}>✓</Typography>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: "#fff" }}>
                  <strong>3 Counselling Sessions</strong> for you
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "0.9rem", color: "#fff" }}>🕛</Typography>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: "#fff" }}>
                  Book within 3 months
                </Typography>
              </Box>
            </Box>
          </Box>


          <Button
            fullWidth
            onClick={onContinue}
            sx={{
              background: "#fff",
              color: "#4072fc",
              fontSize: "14px",
              fontWeight: 600,
              py: 0.8,
              minHeight: 40,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                opacity: 0.95,
              },
              "&.Mui-disabled": {
                background: "#9ca3af",
                color: "#fff",
              },
            }}
          >
            Continue →
          </Button>
        </Card>
      </Box>
    );
  }

  if (normalizedPackage === "premium+") {
    return (

      <Box
        sx={{
          display: "flex",
          boxSizing: "border-box",
          justifyContent: "center",
          alignItems: "flex-start",
          backgroundColor: "#f9fafb",
          py: 2,
          overflow: "hidden",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 600,
            height: { xs: 520, sm: 510 },   // ✅ FIXED HEIGHT
            p: { xs: 1, sm: 2 },
            borderRadius: 4,
            background:
              "linear-gradient(180deg, #6f7de8 0%, #7b4bb7 100%)",
            color: "#fff",
            p: 3,
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, textAlign: "center", }}
          >
            📲 Download MUSICC Wellness App
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: 14,
              opacity: 0.9,
              mb: 1,
            }}
          >
            Continue your healing journey anytime, anywhere!
          </Typography>

          <Box
            sx={{
              display: "inline-block",        // ⬅️ shrink to content
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              px: 2,
              py: 1.2,
              mt: 1.5,
              mb: 2,
              textAlign: "center",
              backgroundColor: "rgba(255,255,255,0.08)",
              mx: "auto",                     // ⬅️ center horizontally
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 15,
                mb: 0.3,
                whiteSpace: "nowrap",         // ⬅️ prevent wrapping
              }}
            >
              🎁 3 Months FREE
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                opacity: 0.9,
                whiteSpace: "nowrap",         // ⬅️ prevent wrapping
              }}
            >
              App subscription included with your package
            </Typography>
          </Box>

          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: 3,
              backgroundColor: "#fff",
              mx: "auto",
              mb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MusicNoteIcon
              sx={{
                fontSize: 50,
                color: "#6f7de8",
              }}
            />
          </Box>

          {/* Store Buttons */}
          {/* Store Buttons */}
          <Stack direction="row" spacing={2} justifyContent="center" mb={2}>
            <Button
              variant="outlined"
              startIcon={<AppleIcon />}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
              }}
              href="https://apps.apple.com/in/app/echo-wellness-sound-sleep/id1585498853"
              target="_blank"
            >
              App Store
            </Button>

            <Button
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
              }}
              href="https://play.google.com/store/apps/details?id=com.diginxthlt.echo"
              target="_blank"
            >
              Google Play
            </Button>
          </Stack>


          {/* Counselling Info */}
          <Box
            sx={{
              height: "150px",
              background: "rgba(255,255,255,0.15)",
              borderRadius: 3,
              p: 1.5,              // ⬅️ slightly more padding
              mb: 1.5,             // ⬅️ more bottom space
            }}
          >
            {/* Title */}
            <Typography
              sx={{
                fontSize: "0.85rem", // ⬅️ slightly bigger
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,            // ⬅️ spacing after title
                color: "#fff",
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 20 }} />
              Counselling Sessions
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontSize: "0.75rem", // ⬅️ better readability
                color: "rgba(255,255,255,0.9)",
                mb: 1,               // ⬅️ spacing after description
                lineHeight: 1.6,
                textAlign: "left",
              }}
            >
              We will send you a WhatsApp link to book your counselling slots.
            </Typography>

            {/* Points */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "0.9rem", color: "#fff" }}>✓</Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#fff" }}>
                  <strong>3 Counselling Sessions</strong> for you
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "0.9rem", color: "#fff" }}>✓</Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#fff" }}>
                  <strong>3 Counselling Sessions</strong> for your Caregiver
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#fff" }}>🕛</Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#fff" }}>
                  Book within 3 months
                </Typography>
              </Box>
            </Box>
          </Box>

          <Button
            fullWidth
            onClick={onContinue}
            sx={{
              background: "#fff",
              color: "#4072fc",
              fontSize: "14px",
              fontWeight: 600,
              py: 0.8,
              minHeight: 40,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                opacity: 0.95,
              },
              "&.Mui-disabled": {
                background: "#9ca3af",
                color: "#fff",
              },
            }}
          >
            Continue →
          </Button>
        </Card>
      </Box>
    );
  }
};




const SessionCompleteUI = ({ onClose }) => {
  const storedCustomer = JSON.parse(localStorage?.getItem("customer") || "{}");
  const package_type = storedCustomer?.package_type;
  const normalizedPackage = package_type?.replace(/\s+/g, "").toLowerCase();
  const showPlanBenefits = normalizedPackage !== "basic";
  const handleClose = () => {
    onClose?.();          // optional (if you still need parent logic)
    // navigate("/");  // 🔁 redirect to login page
  };
  return (
    <Box
      sx={{
        display: "flex",
        boxSizing: "border-box",
        justifyContent: "center",
        alignItems: "flex-start",
        backgroundColor: "#f9fafb",
        py: 2,
        overflow: "hidden",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 640,
          maxHeight: { xs: 520, sm: 520 },
          p: 4,
          borderRadius: 4,
          boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
          textAlign: "center",
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            backgroundColor: "#22c55e",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <Typography sx={{ color: "#fff", fontSize: 40 }}>✓</Typography>
        </Box>

        {/* Title */}
        <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
          🎉 Session Complete!
        </Typography>

        <Typography sx={{ fontSize: 14, color: "#6b7280", mb: 3 }}>
          Great job completing your wellness session!
        </Typography>

        {showPlanBenefits && (
          <>
            {/* Benefits */}
            <Box
              sx={{
                backgroundColor: "#f9fafb",
                borderRadius: 3,
                p: 2.5,
                mb: 3,
                textAlign: "left",
              }}
            >
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                Your Benefits
              </Typography>

              <Typography sx={{ color: "#2563eb", fontWeight: 600, fontSize: 14 }}>
                MUSICC Wellness App – 3 Months
              </Typography>

              {normalizedPackage === "premium" && (<Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                + 3 Counselling Sessions (Book via WhatsApp within 3 months)
              </Typography>)}

              {normalizedPackage === "premium+" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                    + 3 Counselling Sessions for You
                  </Typography>

                  <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                    + 3 Counselling Sessions for Caregiver
                  </Typography>

                  <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                    Book via WhatsApp within 3 months
                  </Typography>
                </Box>
              )}

            </Box>

            {/* WhatsApp Notice */}
            <Box
              sx={{
                backgroundColor: "#eaf7ee",
                borderRadius: 3,
                p: 2,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "center",
                color: "#166534",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 18 }} />
              We’ll send you WhatsApp reminders & booking links!
            </Box>
          </>
        )}

        {/* Close Button */}
        <Button
          fullWidth
          onClick={handleClose}
          sx={{
            background:
              "linear-gradient(90deg, #6f7de8, #7b4bb7)",
            color: "#fff",
            fontWeight: 700,
            py: 1.4,
            borderRadius: 2,
            textTransform: "none",
            "&:hover": {
              opacity: 0.95,
            },
          }}
        >
          Close Portal
        </Button>
      </Card>
    </Box>
  );
};


export default ApolloMusicTherapy;
